import React from 'react';
import { getToken, isTokenExpired, refreshAccessToken } from '../lib/auth.js';
import { emitSession, SessionEvent } from '../lib/session.js';
import { BFF_WS_BASE } from '../lib/runtimeEnv.js';

// Voice → text through the BFF, for dictating a field the dentist would
// otherwise type.
//
// MediaRecorder captures the microphone and each chunk goes as a binary frame to
// parsina.bff.dentist's `/ws/transcribe`, which relays it into the AI service's
// TranscribeAudio stream. The words come back as JSON on the same socket.
//
// ── WHY THIS EXISTS AND useSpeechToText's ENGINE DOES NOT SUFFICE ───────────
// `webkitSpeechRecognition` is absent on Safari and in EVERY browser on iOS
// (they are all WebKit), so the sibling hook could only report `supported:
// false` there and the microphone was hidden on the one device a dentist
// actually holds chairside. MediaRecorder is present in every one of them.
//
// ── IT DOES NOT BEHAVE LIKE THE BROWSER ENGINE, AND THE UI MUST NOT PRETEND ──
// Three differences, all of them visible to a dentist:
//
//   1. NO AUTO-STOP. SpeechRecognition detects the end of an utterance and
//      closes itself; a recorder has no idea whether a silence is a full stop or
//      a breath. Every session here ends when the dentist says it does, which is
//      why `continuous` is accepted and ignored — see below.
//   2. THE WORDS ARRIVE LATE. The service transcribes the accumulated buffer on
//      a timer (HINT_INITIAL_DELAY, then HINT_INTERVAL), so anything under about
//      ten seconds — which is most of شکایت اصلی — produces NO interim text at
//      all. The whole transcript arrives in one piece after `stop()`.
//   3. STOPPING IS NOT INSTANT. The final transcription runs after the audio
//      stops, so there is a wait. `transcribing` is that wait, and a caller that
//      does not render it looks frozen.
//
// ── THE FINAL MESSAGE REPLACES, IT DOES NOT APPEND ──────────────────────────
// The service re-transcribes the WHOLE buffer each time and `is_final` carries
// the entire session, so appending it to anything already shown would say
// everything twice.
//
// ── AND THE INTERIM FRAMES ARE NOT SHOWN AT ALL ────────────────────────────
// They were, until dentists reported their dictation coming back with whole
// phrases repeated:
//
//   «… هنگام خوردن غذاهای سرد یا شیرین درد می‌گیرد غذاهای سرد یا شیرین درد
//    می‌گیرد. همچنین موقع جویدن … دارم همچنین موقع جویدن … دارم»
//
// An interim frame is SUPPOSED to carry only the new words, and this hook
// accumulated them on that promise. But the service computes that delta by
// string comparison against its previous transcription of a SHORTER buffer
// (transcription.py, transcribe_audio: `current_text[len(hint_text_so_far):] if
// current_text.startswith(hint_text_so_far) else current_text`) — and an ASR
// pass over more audio revises what it already heard, so punctuation and
// spelling shift a few characters back and the prefix test fails. The frame
// then carries the WHOLE transcript again, and the accumulator dutifully
// appends it to the copy it already had.
//
// It cannot be repaired from this side: the client cannot tell a genuine new
// phrase from a re-transcription of an old one, because on the wire they are
// the same frame. The words the dentist keeps come from ONE clean pass over the
// whole recording — the final frame — so that is now the only thing surfaced.
// `interim` stays in the return shape, always '', because the browser engine
// (useSpeechToText.js) does produce a sound preview and callers must not have
// to ask which engine they got.
//
// What is lost is the field filling as they speak; what is gained is that it
// fills with what they said. If the live preview is wanted back, the fix is on
// the service: send the cumulative text and let this hook REPLACE rather than
// append — but that frame is shared with the doctor app's live-visit panel.

/**
 * The recording container, in the order we would prefer it.
 *
 * Opus in WebM first because it is the smallest thing that goes over the wire
 * for the same audio, and Chrome/Edge/Firefox all take it. `audio/mp4` last and
 * NOT optional: it is the only entry Safari and iOS accept, and dropping it is
 * exactly how the microphone stops working on an iPad.
 *
 * An empty string means "no candidate matched" — MediaRecorder is then
 * constructed with no options and picks its own, which is the correct fallback
 * rather than a failure.
 */
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];

function pickMime() {
  try {
    return MIME_CANDIDATES.find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || '';
  } catch {
    return '';
  }
}

/**
 * Can this browser record at all?
 *
 * `navigator.mediaDevices` is UNDEFINED outside a secure context, so this is
 * false over plain http on a LAN address — the exact setup someone reaches for
 * when testing on an iPad. `startFailure` below tells those two apart; this
 * predicate only has to answer "is there any point showing a microphone".
 */
export function serverDictationSupported() {
  return typeof window !== 'undefined'
    && !!window.MediaRecorder
    && !!navigator.mediaDevices?.getUserMedia;
}

/** Why a session could not start, in the vocabulary VoiceDictateButton renders. */
function startFailure(err) {
  const name = err?.name || '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') return 'denied';
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') return 'no-mic';
  return 'generic';
}

// The BFF refuses in TWO places at once (src/websocket/transcribeHandler.js): a
// JSON frame carrying a `code`, and then a close code. Both are read, because
// neither alone is reliable — some engines give `onclose` no usable code for a
// close they did not initiate, and a frame can lose the race with the close.
// Whichever arrives first wins; `setError(prev => prev || …)` keeps the second
// from overwriting it with something vaguer.

/** `extensions.code`-style names from the JSON frame. */
const FRAME_REASON = {
  INSUFFICIENT_CREDITS: 'credits',
  UNAUTHENTICATED: 'auth',
  ONBOARDING_REQUIRED: 'onboarding',
  FORBIDDEN: 'onboarding',
  GATE_UNAVAILABLE: 'unavailable',
  AI_UNAVAILABLE: 'unavailable',
};

/** Private-use close codes, mirroring the HTTP statuses the GraphQL side uses. */
const CLOSE_REASON = {
  4401: 'auth',
  4402: 'credits',
  4403: 'onboarding',
  4503: 'unavailable',
  4504: 'unavailable',
};

/**
 * Same signature as useSpeechToText, so the two are interchangeable.
 *
 * @param {object}   opts
 * @param {Function} opts.onResult    called ONCE per session, with the full text
 * @param {boolean}  opts.continuous  accepted and ignored — see the note above.
 *   Kept in the signature so callers do not have to know which engine they got.
 * @returns {{listening: boolean, transcribing: boolean, interim: string,
 *            error: string|null, supported: boolean, start: Function,
 *            stop: Function, toggle: Function}}
 *   `interim` is always '' here — this engine shows the final transcript only.
 */
export default function useServerSpeechToText({ onResult } = {}) {
  const [listening, setListening] = React.useState(false);
  const [transcribing, setTranscribing] = React.useState(false);
  const [error, setError] = React.useState(null);

  const wsRef = React.useRef(null);
  const recorderRef = React.useRef(null);
  const streamRef = React.useRef(null);
  // What the interim frames have said so far. SALVAGE ONLY — it is never shown
  // as a preview (see the header note: the frames overlap, so this text repeats
  // itself). It is what the dentist gets instead of nothing in the two cases
  // where the clean final transcript never arrives: an empty final frame, and
  // the 30-second timeout. A ref and not state because both of those read it
  // from inside a callback.
  const heardRef = React.useRef('');
  // Guards a second socket while the first is still connecting: `listening` is
  // not yet true during the handshake, so a double-tap would open two.
  const openingRef = React.useRef(false);
  const finalTimerRef = React.useRef(null);
  // Chunk sends that have been handed to `arrayBuffer()` but have not reached
  // `ws.send` yet. `{done:true}` must not overtake them — see sendDone.
  const inFlightRef = React.useRef([]);
  const doneSentRef = React.useRef(false);

  const cb = React.useRef(onResult);
  React.useEffect(() => { cb.current = onResult; }, [onResult]);

  /** Drop the microphone tracks — what turns the browser's recording indicator
   *  off. Called AFTER the recorder has finished producing its last chunk. */
  const releaseTracks = React.useCallback(() => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
    streamRef.current = null;
  }, []);

  /** Release the microphone entirely. Separate from teardown: stop() keeps the
   *  socket, because the transcript has not arrived yet. */
  const releaseMic = React.useCallback(() => {
    try {
      const rec = recorderRef.current;
      if (rec && rec.state !== 'inactive') rec.stop();
    } catch { /* already stopped */ }
    releaseTracks();
    recorderRef.current = null;
  }, [releaseTracks]);

  /**
   * Half-close the stream — but only once every chunk already produced is on
   * the wire.
   *
   * THIS ORDERING IS THE WHOLE POINT. `recorder.stop()` emits one last
   * `dataavailable` ASYNCHRONOUSLY, and this hook turns each chunk into an
   * ArrayBuffer asynchronously on top of that. Sending `{done:true}` straight
   * after `stop()` therefore overtakes the final chunk, and the BFF drops
   * anything that arrives after the gRPC stream is half-closed (`!call.writable`)
   * — so the last half-second of every dictation went missing, silently, with a
   * plausible-looking transcript to cover for it.
   *
   * It is worse than half a second on a browser that ignores the timeslice and
   * emits ONLY at stop, which some WebKit builds do: there, that final chunk is
   * the entire recording.
   */
  const sendDone = React.useCallback(async () => {
    if (doneSentRef.current) return;
    doneSentRef.current = true;
    try { await Promise.all(inFlightRef.current); } catch { /* a lost chunk is not a reason to withhold the flush */ }
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ done: true })); } catch { /* the fallback timer covers it */ }
    }
  }, []);

  /** Hard stop: microphone released, socket closed, every flag cleared. */
  const teardown = React.useCallback(() => {
    openingRef.current = false;
    if (finalTimerRef.current) { clearTimeout(finalTimerRef.current); finalTimerRef.current = null; }
    releaseMic();
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) {
      // Detached first: the close below would otherwise re-enter through
      // onclose and clear flags a next session has already set.
      try { ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null; } catch { /* ignore */ }
      try { ws.close(1000, 'client done'); } catch { /* already closed */ }
    }
    setListening(false);
    setTranscribing(false);
  }, [releaseMic]);

  /**
   * The dentist pressed «پایان».
   *
   * The microphone stops here, but the SOCKET STAYS OPEN: `{done:true}` asks the
   * service to transcribe what it has, and the answer is the only complete
   * transcript there will be. Closing now would throw away the whole session.
   *
   * The timer is the promise that this state ends. Without it a service that
   * never answers leaves «در حال تبدیل…» on screen forever, with no way back to
   * the form except a reload.
   */
  const stop = React.useCallback(() => {
    const ws = wsRef.current;
    setListening(false);

    if (ws && ws.readyState === WebSocket.OPEN) {
      setTranscribing(true);

      const recorder = recorderRef.current;
      recorderRef.current = null;
      if (recorder && recorder.state !== 'inactive') {
        // The flush is deferred to `onstop`, which fires AFTER the recorder's
        // last `dataavailable`. The tracks are dropped there too, not here: a
        // microphone cut before the recorder has finished writing its final
        // chunk is a truncated chunk.
        recorder.onstop = () => { releaseTracks(); sendDone(); };
        try { recorder.stop(); } catch { releaseTracks(); sendDone(); }
      } else {
        releaseTracks();
        sendDone();
      }

      if (finalTimerRef.current) clearTimeout(finalTimerRef.current);
      finalTimerRef.current = setTimeout(() => {
        // Whatever the interims managed to say is better than nothing, and the
        // dentist can see it was cut short.
        const partial = heardRef.current.trim();
        if (partial) cb.current?.(partial);
        else setError('timeout');
        teardown();
      }, 30000);
    } else {
      releaseMic();
      teardown();
    }
  }, [releaseMic, releaseTracks, sendDone, teardown]);

  const start = React.useCallback(async () => {
    if (openingRef.current || wsRef.current) return;
    openingRef.current = true;
    setError(null);
    heardRef.current = '';
    inFlightRef.current = [];
    doneSentRef.current = false;

    if (!serverDictationSupported()) {
      // Almost always the insecure-origin case in practice: `mediaDevices` is
      // undefined over plain http, which is how an iPad pointed at a laptop's
      // dev server behaves.
      setError(window.isSecureContext === false ? 'insecure' : 'unsupported');
      openingRef.current = false;
      return;
    }

    // Asked for BEFORE the socket, so a dentist who declines the permission
    // prompt never opens one — and, more to the point, never starts a gRPC
    // stream that bills them for silence.
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      setError(startFailure(err));
      openingRef.current = false;
      return;
    }
    streamRef.current = stream;

    // A WebSocket cannot carry an Authorization header, so the token rides in
    // the query string. Refreshed first: the BFF closes an expired one with
    // 4401 and the dentist would read "log in again" mid-visit for a session
    // that was one refresh away from working.
    let token = getToken();
    if (!token || isTokenExpired(token)) {
      const ok = await refreshAccessToken();
      token = ok ? getToken() : null;
    }
    if (!token) {
      // The session is over, not merely this feature. Say so once, centrally:
      // the shell raises the expired dialog, so the dentist is told what
      // happened instead of being left with a microphone that silently refuses
      // — and the visit on screen behind it is not destroyed. The local `auth`
      // error still renders, for the case where the shell is not mounted.
      emitSession(SessionEvent.EXPIRED);
      setError('auth');
      teardown();
      return;
    }

    let ws;
    try {
      ws = new WebSocket(`${BFF_WS_BASE}/ws/transcribe?token=${encodeURIComponent(token)}`);
    } catch {
      setError('unavailable');
      teardown();
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      // The recorder starts only once the socket is up. Started earlier, its
      // first chunks — which for MPEG-4 carry the header the rest of the file is
      // undecodable without — would be produced with nowhere to go.
      const mimeType = pickMime();
      let recorder;
      try {
        recorder = new window.MediaRecorder(stream, mimeType ? { mimeType } : {});
      } catch {
        setError('unsupported');
        teardown();
        return;
      }
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (!e.data?.size || ws.readyState !== WebSocket.OPEN) return;
        // Blob → ArrayBuffer explicitly. Sending the Blob works in Chrome but
        // Safari's WebSocket is unreliable with one, and the failure is a socket
        // that stays open sending nothing.
        //
        // The promise is REGISTERED, not just fired: `{done:true}` waits on
        // these, or it overtakes the chunk it is meant to follow. See sendDone.
        const sent = e.data.arrayBuffer().then(
          (buf) => { try { ws.send(buf); } catch { /* closing */ } },
          () => { /* the chunk is lost; the next one still counts */ },
        );
        inFlightRef.current.push(sent);
      };
      recorder.start(500);
      openingRef.current = false;
      setListening(true);
    };

    ws.onmessage = (event) => {
      let msg;
      try { msg = JSON.parse(typeof event.data === 'string' ? event.data : ''); } catch { return; }
      if (!msg) return;

      if (msg.error) {
        setError(FRAME_REASON[msg.code] || 'generic');
        teardown();
        return;
      }

      if (typeof msg.text !== 'string') return;

      if (msg.is_final) {
        // The whole session in one clean pass, not a last fragment — and the
        // ONLY thing the dentist is shown. The fallback behind it is the
        // accumulated interims, which repeat themselves; that is a salvage of a
        // session that would otherwise be lost, not a second display path.
        const full = msg.text.trim() || heardRef.current.trim();
        if (full) cb.current?.(full);
        teardown();
        return;
      }
      // Kept, not shown. See heardRef's declaration and the header note.
      heardRef.current = `${heardRef.current}${msg.text} `;
    };

    ws.onerror = () => {
      // A socket error with a transcript already in hand is not worth an error
      // message — the dentist has their words. This fires alongside onclose, so
      // teardown is left to that one handler.
      setError((prev) => prev || 'unavailable');
    };

    ws.onclose = (event) => {
      const reason = CLOSE_REASON[event?.code];
      if (reason) setError((prev) => prev || reason);
      teardown();
    };
  }, [teardown]);

  const toggle = React.useCallback(() => {
    if (wsRef.current) stop(); else start();
  }, [start, stop]);


  // A recorder left running past unmount holds the tab's microphone indicator.
  React.useEffect(() => teardown, [teardown]);

  return {
    listening,
    transcribing,
    // ALWAYS EMPTY on this engine, and kept in the shape rather than dropped so
    // callers stay engine-agnostic — the browser engine's preview is sound and
    // still streams. See the header note for why this one cannot be.
    interim: '',
    error,
    supported: serverDictationSupported(),
    start,
    stop,
    toggle,
  };
}
